import React, { useContext, useEffect, useState } from "react";
import PageLoader from "../../components/PageLoader";
import { useDispatch, connect } from "react-redux";
import { Dispatch, Action } from "redux";
import { getAllStatics } from "../../store/actions/statics";
import useIsMobile from "../../hooks/deviceChecker";
import { useLocation } from 'react-router-dom';

declare global {
  interface Document {
    mozFullScreen: any;
    webkitIsFullScreen: any;
    cancelFullScreen;
    mozCancelFullScreen;
    webkitCancelFullScreen;
  }

  interface HTMLElement {
    mozRequestFullScreen: any;
    webkitRequestFullScreen: any;
  }
}

interface Layout {
  showSubHeader: boolean;
  showBackButton: boolean;
  showSearchBar: boolean;
  showFilters: boolean;
  showTitle: boolean;
  showBreadcrumb: boolean;
  showActions: boolean;

  title: string;
  breadcrumb: string;
  sidebarOpened: boolean;
  fullFillContent: boolean;
}

const initialValues: Layout = {
  showSubHeader: false,
  showBackButton: false,
  showSearchBar: false,
  showFilters: false,
  showTitle: true,
  showBreadcrumb: true,
  showActions: false,

  title: "",
  breadcrumb: "",
  sidebarOpened: true,
  fullFillContent: false,
};

interface ContexInitialValuesDto {
  layout: Layout;
  setLayout: React.Dispatch<React.SetStateAction<Layout>>;
  resetLayout: () => void;
  toggleFullScren: () => void;
  applyedFilters: { [x: string]: (string | number)[] | string };
  setFilters: (
    name: string,
    value: string | number,
    action: "ADD" | "REMOVE"
  ) => void;
  resetFilters: () => void;
  setIsPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const contexInitialValues: ContexInitialValuesDto = {
  layout: initialValues,
  setLayout: () => {},
  resetLayout: () => {},
  toggleFullScren: () => {},
  applyedFilters: {},
  setFilters: () => {},
  resetFilters: () => {},
  setIsPageLoading: () => {},
};


export const LayoutProvidersContext = React.createContext(contexInitialValues);

export const useLayout = () => useContext(LayoutProvidersContext);

const LayoutProvider = ({ children, useLocation, statics, staticsState }) => {
  const [layout, setLayout] = useState<Layout>(initialValues);
  const [applyedFilters, setApplyedFilters] = useState<{
    [x: string]: (string | number)[] | string;
  }>({});
  const dispatch = useDispatch<Dispatch<Action>>();
  const isMobile = useIsMobile();
  const location = useLocation();

  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);

  const resetLayout = () => {
    setLayout(initialValues);
  };

  //   and then we have to send that result as url query params

  // ?enrollment_requirement=Optiona,Mandatory&category=1,2
  const setFilters = (
    name: string,
    value: string | number,
    action: "ADD" | "REMOVE"
  ) => {
    setApplyedFilters((oldState) => {
      // Create a copy of the state object
      const newState: { [x: string]: (string | number)[] | string } = {
        ...oldState,
      };

      // Check if the filter category already exists in the state
      if (!newState[name]) {
        newState[name] = [];
      }

      if (action === "ADD") {
        if (name === "query") {
          newState[name] = [value];
        } else {
          // Add the value to the filter category
          newState[name] = [...(newState[name] as (string | number)[]), value];
        }
      }

      if (action === "REMOVE") {
        if (name === "query") {
          delete newState[name];
        } else {
          // Remove the value from the filter category
          newState[name] = (newState[name] as (string | number)[]).filter(
            (item) => item !== value
          );
        }

        // Remove the filter category if it becomes empty
        if (newState[name].length === 0) {
          delete newState[name];
        }
      }

      return newState;
    });
  };

  const resetFilters = () => setApplyedFilters({});

  const toggleFullScren = () => {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullScreen) {
        document.documentElement.webkitRequestFullScreen();
      }
    } else {
      if (document.cancelFullScreen) {
        document.cancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      }
    }
  };

  // useEffect(() => {
  //   resetLayout();
  // }, [pathname]);

  useEffect(() => {
    if(statics === null && !staticsState.success && !staticsState.loading) {
      dispatch(getAllStatics(() => {
        setLayout({
          ...layout,
          showFilters: location.pathname.includes('/list') ?? false,
          showSearchBar: location.pathname.includes('/list') ?? false,
          showBackButton: location.pathname.includes('/valoration') ?? false,
          showSubHeader: !isMobile,
        })
      }));
    }
  }, [statics]);

  return (
    <LayoutProvidersContext.Provider
      value={{
        layout,
        setLayout,
        toggleFullScren,
        resetLayout,
        applyedFilters,
        setFilters,
        resetFilters,
        setIsPageLoading,
      }}
    >
      {children}

      <PageLoader visible={isPageLoading} />
    </LayoutProvidersContext.Provider>
  );
};

const states = ({ staticsStore }) => {
  const { data: statics, states: staticsState} = staticsStore;

  return {
    statics,
    staticsState
  };
};

export default connect(states)(LayoutProvider);
